'use client';
import React, { useEffect, useRef } from 'react';

const benefits = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="currentColor"/>
      </svg>
    ),
    iconBg: 'bg-theme-04',
    title: 'Live Performance Analytics',
    desc: 'Track GMV, viewer counts, conversion rates, and engagement metrics for every seller in real time.',
    cardBg: 'bg-neutral-01',
    accent: 'bg-theme-04',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" fill="currentColor"/>
      </svg>
    ),
    iconBg: 'bg-theme-03',
    title: 'Smart Matching Engine',
    desc: 'Our AI matches sellers to brands based on niche, audience demographics, GMV history, and brand values.',
    cardBg: 'bg-neutral-01',
    accent: 'bg-theme-03',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
      </svg>
    ),
    iconBg: 'bg-theme-01',
    title: 'Verified Seller Profiles',
    desc: 'Every seller is verified with real TikTok Shop data — no fake followers, no inflated numbers.',
    cardBg: 'bg-neutral-01',
    accent: 'bg-theme-01',
  },
];

export default function BenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.benefit-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="for-sellers" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section title */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="relative">
              <div className="bg-theme-04 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl" style={{ transform: 'rotate(-6deg)', display: 'inline-block' }}>
                Why choose us
              </div>
            </div>
          </div>
          <div className="max-w-[700px]">
            <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-4">
              What makes Talent different
            </h2>
            <p className="text-[18px] text-dark/60 leading-relaxed">
              We built the only platform purpose-designed for TikTok Shop live commerce — connecting verified sellers with brands that are ready to grow.
            </p>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className={`benefit-card ${benefit.cardBg} rounded-[24px] p-8 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              <div className={`w-12 h-12 ${benefit.iconBg} rounded-2xl flex items-center justify-center text-dark mb-5`}>
                {benefit.icon}
              </div>
              <h3 className="text-[18px] font-bold text-dark mb-3">{benefit.title}</h3>
              <p className="text-[15px] text-dark/60 leading-relaxed">{benefit.desc}</p>

              {/* Decorative accent */}
              <div className={`absolute bottom-0 right-0 w-24 h-24 ${benefit.accent} opacity-20 rounded-tl-[60px]`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
